// src/components/RecordingMetadata.js

import React, { useState } from 'react';
import {
  Typography,
  Chip,
  Box,
  LinearProgress,
  Button,
  Card,
  CardContent,
  Stack,
  Divider,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatDuration } from '../utils';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StarRateIcon from '@mui/icons-material/StarRate';
import HistoryIcon from '@mui/icons-material/History';
import TransformIcon from '@mui/icons-material/Transform';
import CreateIcon from '@mui/icons-material/Create';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import EmailIcon from '@mui/icons-material/Email';
import TimerIcon from '@mui/icons-material/Timer';
import ShareIcon from '@mui/icons-material/Share';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import * as amplitude from '@amplitude/analytics-browser';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, updateDoc, serverTimestamp } from 'firebase/firestore';

const RecordingMetadata = ({
  filePath,
  docId,
  duration,
  createdAt,
  fileType,
  status,
  error = '',
  source = "upload",
  userData
}) => {
  const navigate = useNavigate();
  const isLongDuration = duration > 120;
  const auth = getAuth();
  const db = getFirestore();

  // Local override so the UI reflects enabling instantly, before Firestore round-trip
  const [emailNotifEnabled, setEmailNotifEnabled] = useState(null);
  const emailNotifOn = emailNotifEnabled !== null
    ? emailNotifEnabled
    : userData?.emailNotifications_transcriptReady !== false;

  const handleEnableEmailNotif = async () => {
    setEmailNotifEnabled(true);
    amplitude.track('Email Notification Enabled From Waiting Screen', { docId });
    try {
      const user = auth.currentUser;
      if (user) {
        await updateDoc(doc(db, 'users', user.uid), { emailNotifications_transcriptReady: true });
      }
    } catch (e) {
      console.error('Failed to enable email notifications:', e);
    }
  };

  const [rating, setRating] = useState(null);
  const [ratingReasonOpen, setRatingReasonOpen] = useState(false);
  const [ratingThanksOpen, setRatingThanksOpen] = useState(false);
  const [ratingReason, setRatingReason] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleTranscriptRatingChange = async (_, newValue) => {
    if (!newValue || !docId || !auth.currentUser) return;
    setRating(newValue);
    try {
      await updateDoc(doc(db, 'recordings', docId), {
        rating: newValue,
        ratedAt: serverTimestamp(),
        ratedBy: auth.currentUser.uid
      });
    } catch (e) {
      console.error(e);
    }
    if (newValue === 5) {
      amplitude.track('ui_transcript_rating', { rating: newValue, reason: '' });
      setRatingThanksOpen(true);
    } else {
      setRatingReasonOpen(true);
    }
  };

  const handleSubmitLowRating = async () => {
    try {
      await updateDoc(doc(db, 'recordings', docId), {
        ratingReason: (ratingReason || '').trim()
      });
    } catch (e) {
      console.error(e);
    }
    amplitude.track('ui_transcript_rating', { rating: rating || 0, reason: (ratingReason || '').trim() });
    setRatingReasonOpen(false);
    setRatingReason('');
    setSnackbarMsg('Շնորհակալություն կարծիքի համար');
    setSnackbarOpen(true);
  };

  const shareUrl = 'https://xosum.am';
  const shareText = 'Սա ամենալավ հայերեն վերծանիչն է — xosum.am';
  const isM4aConversionCreditsError = error === 'cloudconvert_m4a_insufficient_credits';
  const errorDetailText = String(error || '').trim();
  const supportMailBody =
    `Բարև ձեզ,\n\nՊրոբլեմ է առաջացել ձայնագրության վերծանման ժամանակ:\nԴոկումենտի ID: ${docId}\nՏևողություն: ${formatDuration(duration)}\n`
    + (errorDetailText ? `\nՍխալի տեքստ / error:\n${errorDetailText.slice(0, 1500)}\n` : '')
    + `\nԽնդրում եմ օգնել լուծել այս խնդիրը:\n\nՇնորհակալություն`;

  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'xosum.am', text: shareText, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setSnackbarMsg('Հղումը պատճենվեց');
        setSnackbarOpen(true);
      }
    } catch { }
  };


  const getStatusIcon = () => {
    switch (status) {
      case 'transcribed':
        return <CheckCircleOutlineIcon fontSize="small" />;
      case 'processing':
        return <CreateIcon fontSize="small" />;
      case 'converting':
        return <TransformIcon fontSize="small" />;
      case 'uploading':
        return <CloudUploadIcon fontSize="small" />;
      default:
        return <ReportProblemIcon fontSize="small" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'transcribed':
        return 'success';
      case 'processing':
      case 'converting':
        return 'warning';
      case 'uploading':
        return 'info';
      default:
        return 'error';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'transcribed':
        return 'Վերծանված';
      case 'processing':
        return 'Վերծանվում է';
      case 'converting':
        return 'Փոխակերպվում է';
      case 'uploading':
        return 'Վերբեռնվում է';
      default:
        return 'Սխալ է տեղի ունեցել';
    }
  };



  if (status === 'transcribed') {
    return (
      <Box sx={{ mt: 2, mb: 3 }}>
        <Card elevation={3} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Գնահատե՛ք վերծանված տեքստի որակը
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Սա կօգնի մեզ լավացնել գործիքը
                </Typography>
              </Box>
              <Rating
                name="transcript-rating"
                value={rating || 0}
                onChange={handleTranscriptRatingChange}
                size="large"
              />
            </Stack>
          </CardContent>
        </Card>

        <Dialog
          open={ratingReasonOpen}
          onClose={() => setRatingReasonOpen(false)}
          disableScrollLock
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            Կասե՞ք՝ ինչու չեք գնահատել 5 աստղ
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              value={ratingReason}
              onChange={(e) => setRatingReason(e.target.value)}
              placeholder="Ի՞նչ կարող ենք լավացնել"
              sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={() => setRatingReasonOpen(false)} sx={{ textTransform: 'none', fontWeight: 500 }}>
              Չեղարկել
            </Button>
            <Button onClick={handleSubmitLowRating} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
              Ուղարկել
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={ratingThanksOpen}
          onClose={() => setRatingThanksOpen(false)}
          disableScrollLock
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              position: 'relative',
              zIndex: 10002 // Higher than backdrop to ensure pop-up is not grayed
            }
          }}
          slotProps={{
            backdrop: {
              sx: {
                zIndex: 10001 // Keep backdrop below dialog paper
              }
            }
          }}
        >
          <DialogTitle sx={{ p: 0, position: 'relative' }}>
            <Box
              sx={{
                px: 3, py: 3,
                background: 'linear-gradient(135deg, #FCE7F3 0%, #E0F2FE 100%)',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                borderTopLeftRadius: 8, borderTopRightRadius: 8
              }}
            >
              {/* Close Button (X) - positioned at top right */}
              <IconButton
                onClick={() => setRatingThanksOpen(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>

              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'linear-gradient(135deg, #F59E0B 0%, #FDE68A 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: theme => `0 10px 25px ${theme.palette.warning.main}33`,
                mb: 1.5
              }}>
                <StarRateIcon sx={{ color: 'white', fontSize: 40 }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', textAlign: 'center' }}>
                Շնորհակալություն 5 աստղի համար
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                Օգնեք մեզ տարածել xosum.am-ը՝ մեկ սեղմումով կիսվելով
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="center" alignItems="center">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FacebookIcon />}
                  onClick={() => {
                    amplitude.track('ui_transcript_share', { channel: 'facebook', docId });
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener');
                  }}
                >
                  Facebook
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<LinkedInIcon />}
                  onClick={() => {
                    amplitude.track('ui_transcript_share', { channel: 'linkedin', docId });
                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener');
                  }}
                >
                  LinkedIn
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<TwitterIcon />}
                  onClick={() => {
                    amplitude.track('ui_transcript_share', { channel: 'twitter', docId });
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener');
                  }}
                >
                  X
                </Button>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="center" alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => {
                    amplitude.track('ui_transcript_share', { channel: 'telegram', docId });
                    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
                  }}
                >
                  Telegram
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => {
                    amplitude.track('ui_transcript_share', { channel: 'whatsapp', docId });
                    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, '_blank', 'noopener');
                  }}
                >
                  WhatsApp
                </Button>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ContentCopyIcon />}
                onClick={async () => {
                  amplitude.track('ui_transcript_share', { channel: 'copy_link', docId });
                  await navigator.clipboard.writeText(shareUrl);
                  setSnackbarMsg('Հղումը պատճենվեց');
                  setSnackbarOpen(true);
                }}
                sx={{ textTransform: 'none', mt: 1, px: 3, py: 1 }}
              >
                Պատճենել հղումը
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          message={snackbarMsg}
          action={
            <IconButton size="small" aria-label="close" onClick={handleCloseSnackbar}>
              ✕
            </IconButton>
          }
        />
      </Box>
    );
  }

  return (
    <Card
      elevation={3}
      sx={{
        mt: 2,
        mb: 3,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        overflow: 'visible'
      }}
    >
      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        <Stack spacing={3} alignItems="center">


          {/* Status Chip */}
          <Chip
            icon={getStatusIcon()}
            label={getStatusText()}
            color={getStatusColor()}
            size="large"
            variant="filled"
            sx={{
              height: '36px',
              fontSize: '0.875rem',
              fontWeight: 'medium',
              px: 2
            }}
          />

          {/* Duration Display */}
          {['processing', 'converting'].includes(status) ? (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon color="primary" fontSize="small" />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.primary'
                  }}
                >
                  {formatDuration(duration)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ֆայլի տևողություն
                </Typography>
              </Box>
              <Box sx={{ mt: 1, px: 2 }}>
                {emailNotifOn ? (
                  <Typography variant="body2" color="text.secondary">
                    Մենք կտեղեկացնենք ձեզ էլ. փոստով, երբ այն պատրաստ լինի, և դուք կարող եք ապահով փակել այս էջը:
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: 'warning.soft',
                      border: '1px solid',
                      borderColor: 'warning.main',
                      borderRadius: 2,
                      px: 2.5,
                      py: 1.5,
                      mt: 0.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="warning" />
                      <Typography variant="body2" color="text.secondary">
                        Էլ. փոստի ծանուցումն անջատված է։ Դուք չեք ստանա ծանուցում, երբ վերծանումը ավարտվի:
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<EmailIcon fontSize="small" />}
                      onClick={handleEnableEmailNotif}
                      sx={{ borderRadius: 2 }}
                    >
                      Միացնել ծանուցումը
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon color="primary" fontSize="small" />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.primary'
                }}
              >
                {formatDuration(duration)}
              </Typography>
            </Box>
          )}

          {/* Processing States */}
          {['processing', 'converting'].includes(status) && (
            <>
              <Divider sx={{ width: '100%', my: 2 }} />

              {status === 'converting' && (
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  <Typography
                    variant="h6"
                    color="info.main"
                    sx={{ fontWeight: 'bold', mb: 2 }}
                  >
                    Ձայնագրությունը պատրաստվում է վերծանման
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Ձայնագրությունը փոխակերպվում է վերծանման համար հարմար ֆորմատի
                  </Typography>

                  <LinearProgress
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 6,
                      },
                      mb: 2
                    }}
                  />
                </Box>
              )}

              {status === 'processing' && (
                <>
                  {isLongDuration ? (
                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                      <Typography
                        variant="h6"
                        color="warning.main"
                        sx={{ fontWeight: 'bold', mb: 2 }}
                      >
                        Վերծանումը ընթացքում է
                      </Typography>



                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="center"
                        sx={{ width: '100%' }}
                      >
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={() => navigate(source === "recording" ? '/record' : '/upload')}
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            borderRadius: '8px',
                            px: 3,
                            py: 1,
                            minWidth: '160px'
                          }}
                        >
                          {source === "recording" ? "Նոր ձայնագրություն" : "Վերբեռնել նոր ձայնագրություն"}
                        </Button>

                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={() => navigate('/history')}
                          startIcon={<HistoryIcon />}
                          sx={{
                            borderRadius: '8px',
                            px: 3,
                            py: 1,
                            minWidth: '160px'
                          }}
                        >
                          Դիտել պատմությունը
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h6"
                        color="text.primary"
                        sx={{ mb: 2, fontWeight: 'bold' }}
                      >
                        Վերծանումը ընթացքում է...
                      </Typography>

                      <LinearProgress
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 6,
                          },
                          mb: 2
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {/* Failed State */}
          {status === 'failed' && (
            <>
              <Divider sx={{ width: '100%', my: 2 }} />

              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Typography
                  variant="h6"
                  color="error.main"
                  sx={{ fontWeight: 'bold', mb: 2 }}
                >
                  Վերծանումը չհաջողվեց
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                >
                  {isM4aConversionCreditsError ? (
                    <>
                      Մենք չկարողացանք փոխակերպել ձեր m4a ֆայլը։ Խնդրում ենք գնացեք
                      <a
                        href="https://cloudconvert.com/m4a-to-mp3"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        https://cloudconvert.com/m4a-to-mp3
                      </a>{' '}
                      և փոխակերպեք ֆայը mp3 ֆորմատի.
                    </>
                  ) : (
                    'Ցավոք, տեխնիկական սխալի պատճառով ձայնագրությունը չհաջողվեց վերծանել'
                  )}
                </Typography>

                {errorDetailText && (
                  <Box
                    sx={{
                      textAlign: 'left',
                      width: '100%',
                      maxWidth: 640,
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Տեխնիկական մանրամասներ (կարող եք պատճենել աջակցության համար)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: '0.8125rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {error}
                    </Box>
                  </Box>
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent="center"
                  sx={{ width: '100%' }}
                >
                  <Button
                    variant="contained"
                    size="medium"
                    component="a"
                    href={`mailto:support@xosum.am?subject=${encodeURIComponent(`Վերծանման սխալ - ${docId}`)}&body=${encodeURIComponent(supportMailBody)}`}
                    startIcon={<EmailIcon />}
                    sx={{
                      borderRadius: '8px',
                      px: 3,
                      py: 1,
                      minWidth: '160px'
                    }}
                  >
                    Գրել աջակցության ծառայությանը
                  </Button>

                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={() => navigate(source === "recording" ? '/record' : '/upload')}
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      borderRadius: '8px',
                      px: 3,
                      py: 1,
                      minWidth: '160px'
                    }}
                  >
                    {source === "recording" ? "Նոր ձայնագրություն" : "Նորից փորձել"}
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default RecordingMetadata;
